import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, AfterViewInit, ViewChild, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription, of, forkJoin } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { RequestedTestsComponent } from './requested-tests/requested-tests.component';
import { MixabilityComponent } from './mixability/mixability.component';
import { RedesignComponent } from './redesign/redesign.component';
import { BatchesComponent } from './batches/batches.component';
import {
    SpacerService,
    ProjectStatuses,
    SlurryStatuses,
    UpdateSpacerRequest,
    UpdateSpacerMaterialDto,
} from '../../services/spacer.service';
import { DataGridComponent, GridColumn, GridConfig } from '../../shared/data-grid/data-grid.component';
import { LookupService, MaterialSearchResult, MaterialLookup } from '../../services/lookup.service';
import { SpacerAppendNoteDialogComponent } from './spacer-append-note-dialog/spacer-append-note-dialog.component';
import { SpacerCisproContainersDialogComponent } from './spacer-cispro-containers-dialog/spacer-cispro-containers-dialog.component';
import { CisProSearchComponent } from '../cispro-search/cispro-search.component';
import { CisProSearchResult } from '../../services/cispro-search.service';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { FinalReportDialogComponent, FinalReportDialogData } from '../final-report-dialog/final-report-dialog.component';
import { LoggedUserService } from '../../services/logged-user.service';
import { AddTestDialogComponent, AddTestDialogData, AddTestDialogResult } from '../add-test-dialog/add-test-dialog.component';
import { ProjectDetailsService, SpacerQaqcTitleDto } from '../../services/project-details.service';

interface DryMaterial {
    id?: number;
    name: string;
    specificGravity: number;  // Full precision specific gravity
    absVolume: number;        // Display only - calculated from specificGravity
    weight: number;
    volume: number;
    labWeight: number;
    cisproContainer: string;
}

interface LiquidMaterial {
    id?: number;
    name: string;
    volume: number;
    specificGravity: number;
    weight: number;
    labWeight: number;
    cisproContainer: string;
}

@Component({
    selector: 'app-spacer-details',
    imports: [
        CommonModule,
        FormsModule,
        MatIconModule,
        SpinnerComponent,
        RequestedTestsComponent,
        MixabilityComponent,
        RedesignComponent,
        BatchesComponent,
        DataGridComponent,
        FinalReportDialogComponent
    ],
    templateUrl: './spacer-details.component.html',
    styleUrls: ['./spacer-details.component.css']
})
export class SpacerDetailsComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() projectId: number | null = null;
    @Input() spacerId: number | null = null;
    @Output() projectSaved = new EventEmitter<void>();

    isLoading = true;
    isSaving = false;
    saveMessage: { text: string; isError: boolean } | null = null;
    activeTab = 'spacerInfo';
    private readonly allowedTabs = new Set(['spacerInfo', 'requestedTests', 'mixability', 'redesign', 'batches']);

    ufid = '';
    status = '';
    projectName = '';

    project: any;
    selectedSpacer: any;
    isBlendTest = false;
    spacerQAQCTitlesFromParent: SpacerQaqcTitleDto[] = [];

    readonly ProjectStatuses = ProjectStatuses;
    readonly SlurryStatuses = SlurryStatuses;

    // Track if tests exist (for tab visibility)
    hasTests = false;

    // ── NEW: Track batch existence for canViewBatches parity with WPF ──
    private batchesExist = false;

    // Track if we're viewing batch details
    isViewingBatchDetail = false;

    // ── NEW: Reference to the child BatchesComponent so we can force a
    // reload after status-changing actions (Update/SendToLab/RevertToDraft/Complete)
    @ViewChild(BatchesComponent) batchesComponentRef?: BatchesComponent;

    // ── Data-grid template refs ─────────────────────────────────
    @ViewChild('dryBaseNameTpl') dryBaseNameTpl!: TemplateRef<any>;
    @ViewChild('dryWeightTpl') dryWeightTpl!: TemplateRef<any>;
    @ViewChild('dryAbsVolumeTpl') dryAbsVolumeTpl!: TemplateRef<any>;
    @ViewChild('dryContainerTpl') dryContainerTpl!: TemplateRef<any>;
    @ViewChild('blendAdditiveNameTpl') blendAdditiveNameTpl!: TemplateRef<any>;
    @ViewChild('afterBlendAdditiveNameTpl') afterBlendAdditiveNameTpl!: TemplateRef<any>;
    @ViewChild('liquidBaseNameTpl') liquidBaseNameTpl!: TemplateRef<any>;
    @ViewChild('liquidVolumeTpl') liquidVolumeTpl!: TemplateRef<any>;
    @ViewChild('liquidSgTpl') liquidSgTpl!: TemplateRef<any>;
    @ViewChild('liquidAdditiveNameTpl') liquidAdditiveNameTpl!: TemplateRef<any>;
    @ViewChild('liquidContainerTpl') liquidContainerTpl!: TemplateRef<any>;
    @ViewChild(RequestedTestsComponent) requestedTestsComp?: RequestedTestsComponent;

    // ── Data-grid column definitions ────────────────────────────
    dryBaseColumns: GridColumn[] = [];
    dryAdditivesColumns: GridColumn[] = [];
    afterBlendAdditivesColumns: GridColumn[] = [];
    liquidBaseColumns: GridColumn[] = [];
    liquidAdditivesColumns: GridColumn[] = [];

    readonly materialGridConfig: GridConfig = {
        pageSize: 0,
        showColumnFilters: false,
        enableSorting: false,
        stickyHeader: false
    };

    // ── Single-row data arrays for base rows ─────────────────────
    blendBaseRow: DryMaterial[] = [];
    rigFreshWaterRow: LiquidMaterial[] = [];

    // ── Spacer Info fields ──────────────────────────────────────
    waterFractionBased = false;
    waterFraction: number | null = null;
    viscosifier: number | null = null;
    labVolume: number | null = null;
    totalDensity: number | null = null;

    blendCisproContainer = '';
    spacerComments = '';
    pendingNote = '';

    /** SlurryStatuses enum (1..7) → display label. */
    private readonly statusLabels: Record<number, string> = {
        1: 'Draft',
        2: 'Ready to Test',
        3: 'Ongoing in Lab',
        4: 'Test Failed',
        5: 'Test Completed',
        6: 'Draft Again',
        7: 'Sent to Lab Again'
    };

    blendBase: DryMaterial = { name: '', specificGravity: 0, absVolume: 0, weight: 0, volume: 0, labWeight: 0, cisproContainer: '' };

    /** Dry materials with afterBlend=false (excluding blend base) */
    blendAdditives: DryMaterial[] = [];
    /** Dry materials with afterBlend=true */
    afterBlendAdditives: DryMaterial[] = [];

    rigFreshWater: LiquidMaterial = { id: 0, name: '', volume: 0, specificGravity: 0, weight: 0, labWeight: 0, cisproContainer: '' };

    liquidAdditives: LiquidMaterial[] = [];

    // ── Base material dropdowns ─────────────────────────────────
    dryBaseOptions: MaterialLookup[] = [];
    liquidBaseOptions: MaterialLookup[] = [];
    selectedDryBaseId: number | null = null;
    selectedLiquidBaseId: number | null = null;

    private allDryBaseOptions: MaterialLookup[] = [];
    private allDryFractionBaseOptions: MaterialLookup[] = [];
    private allLiquidBaseOptions: MaterialLookup[] = [];
    private allLiquidFractionBaseOptions: MaterialLookup[] = [];

    showBlendAdditiveSearch = false;
    blendAdditiveSearchText = '';
    blendAdditiveOptions: MaterialSearchResult[] = [];

    showAfterBlendAdditiveSearch = false;
    afterBlendAdditiveSearchText = '';
    afterBlendAdditiveOptions: MaterialSearchResult[] = [];

    showLiquidAdditiveSearch = false;
    liquidAdditiveSearchText = '';
    liquidAdditiveOptions: MaterialSearchResult[] = [];

    private blendAdditiveSubject = new Subject<string>();
    private afterBlendAdditiveSubject = new Subject<string>();
    private liquidSearchSubject = new Subject<string>();

    private subs = new Subscription();

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private spacerService: SpacerService,
        private projectDetailsService: ProjectDetailsService,
        private lookupService: LookupService,
        private dialog: MatDialog,
        private loggedUserService: LoggedUserService
    ) { }

    ngOnInit(): void {
        // Material search subscriptions
        this.subs.add(
            this.blendAdditiveSubject.pipe(
                debounceTime(300),
                distinctUntilChanged(),
                switchMap(text => text.length >= 1 ? this.lookupService.searchMaterials(text, 1) : of([]))
            ).subscribe(results => {
                const selectedIds = this.getSelectedDryMaterialIds();
                this.blendAdditiveOptions = results.filter(
                    material => !selectedIds.includes(material.id)
                );
            })
        );
        this.subs.add(
            this.afterBlendAdditiveSubject.pipe(
                debounceTime(300),
                distinctUntilChanged(),
                switchMap(text => text.length >= 1 ? this.lookupService.searchMaterials(text, 1) : of([]))
            ).subscribe(results => {
                const selectedIds = this.getSelectedDryMaterialIds();
                this.afterBlendAdditiveOptions = results.filter(
                    material => !selectedIds.includes(material.id)
                );
            })
        );
        this.subs.add(
            this.liquidSearchSubject.pipe(
                debounceTime(300),
                distinctUntilChanged(),
                switchMap(text => text.length >= 1 ? this.lookupService.searchMaterials(text, 2) : of([]))
            ).subscribe(results => {
                const selectedIds = this.getSelectedLiquidMaterialIds();
                this.liquidAdditiveOptions = results.filter(
                    material => !selectedIds.includes(material.id)
                );
            })
        );

        const navState = history.state as {
            projectId?: number;
            projectName?: string;
            blendTest?: boolean;
            slurry?: any;
            spacerQAQCTitles?: SpacerQaqcTitleDto[];
        };

        if (navState?.projectId) {
            this.projectId = navState.projectId;
        }
        if (navState?.projectName) {
            this.projectName = navState.projectName;
        }
        if (navState?.slurry) {
            this.selectedSpacer = navState.slurry;
            this.spacerComments = navState.slurry.comments ?? '';
        }
        if (Array.isArray(navState?.spacerQAQCTitles)) {
            this.spacerQAQCTitlesFromParent = navState.spacerQAQCTitles;
        }
        if (navState?.blendTest !== undefined) {
            this.isBlendTest = navState.blendTest;
        }

        this.route.queryParamMap.subscribe(queryParams => {
            const tab = queryParams.get('tab');
            if (tab && this.allowedTabs.has(tab)) {
                this.activeTab = tab;
            }
        });

        this.route.params.subscribe(params => {
            if (!this.projectId) {
                this.projectId = params['projectId'] ? Number(params['projectId']) : null;
            }
            this.spacerId = params['spacerId'] ? Number(params['spacerId']) : null;
            if (this.spacerId) {
                this.loadSpacerInfo();
            } else {
                this.isLoading = false;
            }
        });
    }

    setTab(tab: string): void {
        this.activeTab = tab;
    }

    goBackToDashboard(): void {
        this.router.navigate(['/projects-dashboard']);
    }

    navigateToProject(): void {
        if (!this.projectId) {
            this.goBackToDashboard();
            return;
        }
        this.router.navigate(['/projects-dashboard'], {
            state: { openProjectId: this.projectId, projectInitialTab: 'project' }
        });
    }

    onBatchDetailViewChange(isInBatchDetail: boolean): void {
        this.isViewingBatchDetail = isInBatchDetail;

        //if existing batch detail(false), reload spacer to get updated status
        if (!isInBatchDetail && this.spacerId) {
            this.loadSpacerInfo(true);
        }
    }

    navigateBackToBatchesList(): void {
        const url = new URL(window.location.href);
        url.searchParams.set('tab', 'batches');
        window.location.assign(url.toString());
    }

    // ── Client-side calculation ─────────────────────────────────
    private readonly WATER_DENSITY = 8.34;
    private readonly SPACER_TOTAL_VOLUME = 42;
    private readonly LB_PER_GAL_IN_G_PER_ML = 0.119826;

    onMaterialValueChange(): void { this.calculate(); }

    /**
     * Returns true when the value contains alphabetic or special characters
     * (i.e. is not a valid number/decimal). Used for fields where only
     * numbers/decimals are allowed but the field may be left empty.
     */
    isNumericInvalid(value: any): boolean {
        if (value === null || value === undefined || value === '') return false;
        return isNaN(Number(value));
    }

    /**
     * Returns true when the value is blank/empty OR contains alphabetic/
     * special characters. Used for Weight (lb) and Volume (gal) which must
     * always have a valid numeric value.
     */
    isRequiredNumericInvalid(value: any): boolean {
        if (value === null || value === undefined || String(value).trim() === '') return true;
        return isNaN(Number(value));
    }

    /**
     * Calculate density from specific gravity with full precision.
     * Matches C# Material.Density property.
     */
    private calculateDensity(specificGravity: number): number {
        return specificGravity * this.WATER_DENSITY;
    }

    /**
     * Calculate absolute volume from specific gravity with full precision.
     * Matches C# Material.AbsoluteVolume property: 1 / Density
     */
    private calculateAbsoluteVolume(specificGravity: number): number {
        const density = this.calculateDensity(specificGravity);
        return density > 0 ? 1 / density : 0;
    }

    private calculate(): void {
        if (!this.labVolume || this.labVolume <= 0) return;

        // Step 1: Dry base weight and volume calculation with full precision
        // Scenario 2A: Weight = Viscosifier × (WaterFraction ÷ 100)
        if (this.waterFractionBased && this.viscosifier != null && this.waterFraction != null && this.selectedDryBaseId != null) {
            this.blendBase.weight = this.viscosifier * (this.waterFraction / 100);
        }

        if (this.selectedDryBaseId != null) {
            // Calculate using full-precision density (matches C# DryMaterial.Volume)
            const density = this.calculateDensity(this.blendBase.specificGravity);
            this.blendBase.volume = density > 0 ? this.blendBase.weight / density : 0;
            // Update displayed absVolume from full-precision calculation
            this.blendBase.absVolume = Math.round(this.calculateAbsoluteVolume(this.blendBase.specificGravity) * 10000) / 10000;
        }

        // Calculate volumes for all additive arrays using full-precision density
        for (const m of this.blendAdditives) {
            const density = this.calculateDensity(m.specificGravity);
            m.volume = density > 0 ? m.weight / density : 0;
            m.absVolume = Math.round(this.calculateAbsoluteVolume(m.specificGravity) * 10000) / 10000;
        }

        for (const m of this.afterBlendAdditives) {
            const density = this.calculateDensity(m.specificGravity);
            m.volume = density > 0 ? m.weight / density : 0;
            m.absVolume = Math.round(this.calculateAbsoluteVolume(m.specificGravity) * 10000) / 10000;
        }

        // Step 2: Liquid additive weights (matches C# LiquidMaterial.Weight)
        for (const m of this.liquidAdditives) {
            m.weight = m.volume * m.specificGravity * this.WATER_DENSITY;
        }

        // Step 3: Liquid base volume calculation
        const dryTotalVolume =
            (this.selectedDryBaseId != null ? this.blendBase.volume : 0) +
            this.blendAdditives.reduce((s, m) => s + m.volume, 0) +
            this.afterBlendAdditives.reduce((s, m) => s + m.volume, 0);
        const liquidAdditiveVolume = this.liquidAdditives.reduce((s, m) => s + m.volume, 0);
        const liquidAdditiveWeight = this.liquidAdditives.reduce((s, m) => s + m.weight, 0);

        let liquidBaseVolume = 0;
        if (this.selectedLiquidBaseId != null) {
            if (this.waterFractionBased && this.waterFraction != null) {
                // Scenario 2B: Volume = 42 × (WaterFraction ÷ 100)
                liquidBaseVolume = Math.max(0,
                    (this.waterFraction / 100) * this.SPACER_TOTAL_VOLUME);
            } else {
                // Scenario 1C: Liquid Base Volume = 42 − (Dry Base Vol + Dry Additives Vol + Liquid Additives Vol)
                // Matches C# DefaultSpacerCalculationStrategy.Recalculate
                liquidBaseVolume = Math.max(0,
                    this.SPACER_TOTAL_VOLUME - dryTotalVolume - liquidAdditiveVolume);
            }
            this.rigFreshWater.volume = liquidBaseVolume;
            this.rigFreshWater.weight = liquidBaseVolume * this.rigFreshWater.specificGravity * this.WATER_DENSITY;
        }

        // Step 4: Totals and density (matches C# SpacerMaterials.TotalDensity)
        const totalDryWeight =
            (this.selectedDryBaseId != null ? this.blendBase.weight : 0) +
            this.blendAdditives.reduce((s, m) => s + m.weight, 0) +
            this.afterBlendAdditives.reduce((s, m) => s + m.weight, 0);
        const liquidBaseWeight = this.selectedLiquidBaseId != null ? this.rigFreshWater.weight : 0;
        const totalWeight = totalDryWeight + liquidBaseWeight + liquidAdditiveWeight;
        const totalVolume = dryTotalVolume +
            (this.selectedLiquidBaseId != null ? liquidBaseVolume : 0) + liquidAdditiveVolume;

        // Calculate total density with full precision for intermediate calculations
        const totalDensityFullPrecision = totalVolume > 0 ? totalWeight / totalVolume : 0;
        this.totalDensity = totalDensityFullPrecision > 0 ? Math.round(totalDensityFullPrecision * 100) / 100 : null;

        // Step 5: Lab weights using full precision density (matches C# Material.LabWeight)
        if (totalWeight > 0 && totalDensityFullPrecision > 0) {
            const factor = totalDensityFullPrecision * this.labVolume * this.LB_PER_GAL_IN_G_PER_ML / totalWeight;
            if (this.selectedDryBaseId != null) {
                this.blendBase.labWeight = Math.round(this.blendBase.weight * factor * 100) / 100;
            }
            for (const m of this.blendAdditives) {
                m.labWeight = Math.round(m.weight * factor * 100) / 100;
            }
            for (const m of this.afterBlendAdditives) {
                m.labWeight = Math.round(m.weight * factor * 100) / 100;
            }
            if (this.selectedLiquidBaseId != null) {
                this.rigFreshWater.labWeight = Math.round(this.rigFreshWater.weight * factor * 100) / 100;
            }
            for (const m of this.liquidAdditives) {
                m.labWeight = Math.round(m.weight * factor * 100) / 100;
            }
        }

        // Force data-grid to re-render computed columns
        this.blendBaseRow = [...this.blendBaseRow];
        this.blendAdditives = [...this.blendAdditives];
        this.afterBlendAdditives = [...this.afterBlendAdditives];
        this.rigFreshWaterRow = [...this.rigFreshWaterRow];
        this.liquidAdditives = [...this.liquidAdditives];
    }

    ngAfterViewInit(): void {
        const dryCommon = (nameTpl: TemplateRef<any>): GridColumn[] => [
            { key: 'name', label: '', width: '220px', customTemplate: nameTpl },
            { key: 'absVolume', label: 'Abs. volume (gal/lb)', cellAlign: 'right', customTemplate: this.dryAbsVolumeTpl },
            { key: 'weight', label: 'Weight (lb)', cellAlign: 'right', customTemplate: this.dryWeightTpl },
            {
                key: 'volume', label: 'Volume (gal)', cellAlign: 'right',
                accessor: (item: DryMaterial) => (item.volume ?? 0).toFixed(3)
            },
            {
                key: 'labWeight', label: 'Lab weight (g)', cellAlign: 'right',
                accessor: (item: DryMaterial) => (item.labWeight ?? 0).toFixed(2)
            },
            { key: 'cisproContainer', label: 'CisPRO Container', customTemplate: this.dryContainerTpl }
        ];
        this.dryBaseColumns = dryCommon(this.dryBaseNameTpl);
        this.dryAdditivesColumns = dryCommon(this.blendAdditiveNameTpl);
        this.afterBlendAdditivesColumns = dryCommon(this.afterBlendAdditiveNameTpl);

        this.liquidBaseColumns = [
            { key: 'name', label: '', width: '220px', customTemplate: this.liquidBaseNameTpl },
            {
                key: 'volume', label: 'Volume (gal)', cellAlign: 'right',
                accessor: (item: LiquidMaterial) => (item.volume ?? 0).toFixed(3)
            },
            { key: 'specificGravity', label: 'Specific gravity', cellAlign: 'right', customTemplate: this.liquidSgTpl },
            {
                key: 'weight', label: 'Weight (lb)', cellAlign: 'right',
                accessor: (item: LiquidMaterial) => (item.weight ?? 0).toFixed(3)
            },
            {
                key: 'labWeight', label: 'Lab weight (g)', cellAlign: 'right',
                accessor: (item: LiquidMaterial) => (item.labWeight ?? 0).toFixed(2)
            },
            { key: 'cisproContainer', label: 'CisPRO Container', customTemplate: this.liquidContainerTpl }
        ];

        this.liquidAdditivesColumns = [
            { key: 'name', label: '', width: '220px', customTemplate: this.liquidAdditiveNameTpl },
            { key: 'volume', label: 'Volume (gal)', cellAlign: 'right', customTemplate: this.liquidVolumeTpl },
            { key: 'specificGravity', label: 'Specific gravity', cellAlign: 'right', customTemplate: this.liquidSgTpl },
            {
                key: 'weight', label: 'Weight (lb)', cellAlign: 'right',
                accessor: (item: LiquidMaterial) => (item.weight ?? 0).toFixed(3)
            },
            {
                key: 'labWeight', label: 'Lab weight (g)', cellAlign: 'right',
                accessor: (item: LiquidMaterial) => (item.labWeight ?? 0).toFixed(2)
            },
            { key: 'cisproContainer', label: 'CisPRO Container', customTemplate: this.liquidContainerTpl }
        ];
    }

    loadSpacerInfo(silent = false): void {
        if (!this.spacerId) return;
        if (!silent) this.isLoading = true;

        // Re-use cached lookups on subsequent calls (saves only need the spacer endpoint)
        const lookups$ = this.allDryBaseOptions.length > 0
            ? of({
                dryBase: this.allDryBaseOptions,
                dryFractionBase: this.allDryFractionBaseOptions,
                liquidBase: this.allLiquidBaseOptions,
                liquidFractionBase: this.allLiquidFractionBaseOptions
            })
            : forkJoin({
                dryBase: this.lookupService.getMaterialsByTypeId(7),
                dryFractionBase: this.lookupService.getMaterialsByTypeId(8),
                liquidBase: this.lookupService.getMaterialsByTypeId(9),
                liquidFractionBase: this.lookupService.getMaterialsByTypeId(10)
            });

        const projectHeader$ = (this.projectId && !this.projectName?.trim())
            ? this.projectDetailsService.getProjectById(this.projectId)
            : of(null);

        forkJoin({
            spacer: this.spacerService.getByIdWithTestsLoaded(this.spacerId),
            lookups: lookups$,
            projectHeader: projectHeader$,
            // NEW: lightweight batch-existence check, kept in sync with the mix on every load/reload
            hasBatches: this.spacerService.hasBatches(this.spacerId)
        }).subscribe({
            next: ({ spacer: data, lookups: { dryBase, dryFractionBase, liquidBase, liquidFractionBase }, projectHeader, hasBatches }) => {
                this.allDryBaseOptions = dryBase;
                this.allDryFractionBaseOptions = dryFractionBase;
                this.allLiquidBaseOptions = liquidBase;
                this.allLiquidFractionBaseOptions = liquidFractionBase;

                // NEW: keep batch-existence flag in sync so canViewBatches matches WPF logic
                this.batchesExist = !!hasBatches;

                this.selectedSpacer = {
                    ...data,
                    statusLabel: this.statusLabels[data.status] ?? String(data.status)
                };
                this.ufid = data.ufSlurryID ?? '';
                this.status = this.statusLabels[data.status] ?? String(data.status);
                this.projectId = this.projectId ?? data.cementProjID ?? null;

                if (!this.projectName?.trim() && projectHeader?.name) {
                    this.projectName = projectHeader.name;
                }

                if (!this.projectName?.trim()) {
                    this.loadProjectHeaderDetails();
                }

                // Check if tests exist (for tab visibility - matches WPF SecurityController line 205)
                this.hasTests = data.spacerTests != null &&
                    (data.spacerTests.spacerQaQc != null ||
                        data.spacerTests.spacerCompatibility != null ||
                        data.spacerTests.spacerSsst != null ||
                        (data.spacerTests.spacerHpHtRheologyInputs != null && data.spacerTests.spacerHpHtRheologyInputs.length > 0));

                this.waterFraction = data.waterFraction ?? null;
                this.viscosifier = data.viscosifier ?? null;
                this.waterFractionBased =
                    (data.waterFraction != null && data.waterFraction !== 0) ||
                    (data.viscosifier != null && data.viscosifier !== 0);
                this.labVolume = data.targetLabVolume ?? null;
                this.totalDensity = data.slurryDensity ?? null;
                this.blendCisproContainer = data.preferredBlendCisPROContainerID ?? '';
                this.spacerComments = data.comments ?? '';

                this.dryBaseOptions = this.waterFractionBased
                    ? [...this.allDryFractionBaseOptions]
                    : [...this.allDryBaseOptions];
                this.liquidBaseOptions = this.waterFractionBased
                    ? [...this.allLiquidFractionBaseOptions]
                    : [...this.allLiquidBaseOptions];

                if (data.materials?.length) {
                    const blendBaseData = data.materials.find(
                        (m: any) => m.sectionType === 'SpacerDryBase' || m.sectionType === 'SpacerDryFractionBase'
                    );

                    if (blendBaseData) {
                        this.selectedDryBaseId = blendBaseData.materialID;

                        // Get specificGravity with full precision from lookup or calculate from absoluteVolume
                        const lookupMaterial = this.dryBaseOptions.find(m => m.id === blendBaseData.materialID);
                        const specificGravity = lookupMaterial?.specificGravity ??
                            (blendBaseData.absoluteVolume > 0 ? 1 / (blendBaseData.absoluteVolume * this.WATER_DENSITY) : 0);

                        if (!this.dryBaseOptions.some(m => m.id === blendBaseData.materialID)) {
                            this.dryBaseOptions = [
                                ...this.dryBaseOptions,
                                {
                                    id: blendBaseData.materialID,
                                    name: blendBaseData.name,
                                    materialTypeId: blendBaseData.materialType,
                                    absoluteVolume: blendBaseData.absoluteVolume ?? 0,
                                    specificGravity: specificGravity
                                }
                            ];
                        }

                        this.blendBase = {
                            id: blendBaseData.materialID,
                            name: blendBaseData.name ?? '',
                            specificGravity: specificGravity,
                            absVolume: Math.round((blendBaseData.absoluteVolume ?? 0) * 10000) / 10000,
                            weight: blendBaseData.weight ?? 0,
                            volume: blendBaseData.volume ?? 0,
                            labWeight: blendBaseData.calculatedLabWeight ?? 0,
                            cisproContainer: blendBaseData.preferredCisPROContainerIDs ?? ''
                        };

                        this.blendBaseRow = [this.blendBase];
                    }

                    // const mapDry = (m: any) => {
                    //     // Get specificGravity from search results or calculate from absoluteVolume
                    //     const specificGravity = m.absoluteVolume > 0 
                    //         ? 1 / (m.absoluteVolume * this.WATER_DENSITY) 
                    //         : 0;

                    //     return {
                    //         id: m.materialID,
                    //         name: m.name ?? '',
                    //         specificGravity: specificGravity,
                    //         absVolume: m.absoluteVolume ?? 0,
                    //         weight: m.weight ?? 0,
                    //         volume: m.volume ?? 0,
                    //         labWeight: m.calculatedLabWeight ?? 0,
                    //         cisproContainer: m.preferredCisPROContainerIDs ?? ''
                    //     };
                    // };
                    const mapDry = (m: any) => {
                        // Priority: Use specificGravity from API, calculate from absoluteVolume as fallback
                        let specificGravity = m.specificGravity ?? m.overRideSP ?? 0;
                        let absVolume = m.absoluteVolume ?? 0;

                        // If we have SG but no absVolume, calculate it
                        if (specificGravity > 0 && absVolume === 0) {
                            absVolume = 1 / (specificGravity * this.WATER_DENSITY);
                        }
                        // If we have absVolume but no SG, calculate it
                        else if (absVolume > 0 && specificGravity === 0) {
                            specificGravity = 1 / (absVolume * this.WATER_DENSITY);
                        }

                        return {
                            id: m.materialID,
                            name: m.name ?? '',
                            specificGravity: specificGravity,
                            absVolume: Math.round(absVolume * 10000) / 10000,
                            weight: m.weight ?? 0,
                            volume: m.volume ?? 0,
                            labWeight: m.calculatedLabWeight ?? 0,
                            cisproContainer: m.preferredCisPROContainerIDs ?? ''
                        };
                    };

                    if (this.isBlendTest) {
                        this.blendAdditives = data.materials
                            .filter((m: any) => m.sectionType === 'Dry' && !m.afterBlend)
                            .map(mapDry);
                        this.afterBlendAdditives = data.materials
                            .filter((m: any) => m.sectionType === 'Dry' && m.afterBlend)
                            .map(mapDry);
                    } else {
                        // Non-blend test: "Blend Additives" section is hidden (@if isBlendTest),
                        // so route ALL dry additives into afterBlendAdditives ("Additives" section)
                        this.blendAdditives = [];
                        this.afterBlendAdditives = data.materials
                            .filter((m: any) => m.sectionType === 'Dry')
                            .map(mapDry);
                    }

                    const waterData = data.materials.find(
                        (m: any) => m.sectionType === 'SpacerLiquidBase' || m.sectionType === 'SpacerLiquidFractionBase'
                    );

                    if (waterData) {
                        this.selectedLiquidBaseId = waterData.materialID;

                        if (!this.liquidBaseOptions.some(m => m.id === waterData.materialID)) {
                            this.liquidBaseOptions = [
                                ...this.liquidBaseOptions,
                                {
                                    id: waterData.materialID,
                                    name: waterData.name,
                                    materialTypeId: waterData.materialType,
                                    absoluteVolume: 0,
                                    specificGravity: waterData.overRideSP
                                }
                            ];
                        }

                        this.rigFreshWater = {
                            id: waterData.materialID,
                            name: waterData.name ?? '',
                            volume: waterData.volume ?? 0,
                            specificGravity: waterData.overRideSP ?? 0,
                            weight: waterData.weight ?? 0,
                            labWeight: waterData.calculatedLabWeight ?? 0,
                            cisproContainer: waterData.preferredCisPROContainerIDs ?? ''
                        };

                        this.rigFreshWaterRow = [this.rigFreshWater];
                    }

                    this.liquidAdditives = data.materials
                        .filter((m: any) => m.sectionType === 'Liquid')
                        .map((m: any) => ({
                            id: m.materialID,
                            name: m.name ?? '',
                            volume: m.volume ?? 0,
                            specificGravity: m.overRideSP ?? 0,
                            weight: m.weight ?? 0,
                            labWeight: m.calculatedLabWeight ?? 0,
                            cisproContainer: m.preferredCisPROContainerIDs ?? ''
                        }));
                }

                this.calculate();
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
            }
        });
    }

    private loadProjectHeaderDetails(): void {
        if (!this.projectId || this.projectId <= 0) {
            return;
        }

        this.projectDetailsService.getProjectById(this.projectId).subscribe({
            next: (project) => {
                this.projectName = this.projectName || project?.name || '';
            },
            error: (err) => {
                console.error('Failed to load project details for spacer header:', err);
            }
        });
    }

    /** Matches WPF line 86-88: Only new spacers can toggle fraction-based */
    onWaterFractionBasedChange(): void {
        if (this.spacerId) {
            this.waterFractionBased = !this.waterFractionBased;
            this.dialog.open(ConfirmDialogComponent, {
                width: '380px',
                data: {
                    title: 'Not Allowed',
                    message: 'Cannot change Water Fraction Based for existing spacers.',
                    confirmLabel: 'OK',
                    cancelLabel: ''
                }
            });
            return;
        }

        this.dryBaseOptions = this.waterFractionBased
            ? [...this.allDryFractionBaseOptions]
            : [...this.allDryBaseOptions];
        this.liquidBaseOptions = this.waterFractionBased
            ? [...this.allLiquidFractionBaseOptions]
            : [...this.allLiquidBaseOptions];

        this.selectedDryBaseId = null;
        this.selectedLiquidBaseId = null;
        this.blendBase = { name: '', specificGravity: 0, absVolume: 0, weight: 0, volume: 0, labWeight: 0, cisproContainer: '' };
        this.blendBaseRow = [this.blendBase];
        this.rigFreshWater = { name: '', volume: 0, specificGravity: 0, weight: 0, labWeight: 0, cisproContainer: '' };
        this.rigFreshWaterRow = [this.rigFreshWater];
        this.calculate();
    }

    onDryBaseSelected(id: number | null): void {
        const material = this.dryBaseOptions.find(m => m.id === id);
        if (material) {
            this.blendBase = {
                id: material.id,
                name: material.name ?? '',
                specificGravity: material.specificGravity ?? 0,
                absVolume: Math.round((material.absoluteVolume ?? 0) * 10000) / 10000,
                weight: 0,
                volume: 0,
                labWeight: 0,
                cisproContainer: ''
            };
        } else {
            this.blendBase = { name: '', specificGravity: 0, absVolume: 0, weight: 0, volume: 0, labWeight: 0, cisproContainer: '' };
        }
        this.blendBaseRow = [this.blendBase];
        this.calculate();
    }

    onLiquidBaseSelected(id: number | null): void {
        const material = this.liquidBaseOptions.find(m => m.id === id);
        if (material) {
            this.rigFreshWater = {
                id: material.id,
                name: material.name ?? '',
                specificGravity: material.specificGravity ?? 0,
                volume: 0,
                weight: 0,
                labWeight: 0,
                cisproContainer: ''
            };
        } else {
            this.rigFreshWater = { name: '', volume: 0, specificGravity: 0, weight: 0, labWeight: 0, cisproContainer: '' };
        }
        this.rigFreshWaterRow = [this.rigFreshWater];
        this.calculate();
    }

    appendNote(): void {
        if (!this.spacerId) return;
        this.spacerService.appendNote(this.spacerId, this.pendingNote).subscribe({
            next: () => {
                this.pendingNote = '';
                this.loadSpacerInfo();
            }
        });
    }

    clearNote(): void {
        if (!this.spacerId) return;
        this.spacerService.clearNote(this.spacerId).subscribe({
            next: () => { this.loadSpacerInfo(); }
        });
    }

    searchCispro(): void {
        const dialogRef = this.dialog.open(CisProSearchComponent, {
            width: '90%',
            maxWidth: '1300px',
            maxHeight: '85vh',
            panelClass: 'cispro-search-dialog'
        });

        dialogRef.afterClosed().subscribe((result: CisProSearchResult | undefined) => {
            if (result) {
                this.blendCisproContainer = result.barcodeId;
            }
        });
    }

    removeBlendCispro(): void {
        this.blendCisproContainer = '';
    }

    showAddBlendAdditiveSearch(): void {
        this.showBlendAdditiveSearch = true;
        this.blendAdditiveSearchText = '';
        this.blendAdditiveOptions = [];
    }
    onBlendAdditiveSearch(text: string): void { this.blendAdditiveSubject.next(text); }
    selectBlendAdditive(material: MaterialSearchResult): void {
        const alreadyExists =
            this.blendAdditives.some(x => x.id === material.id) ||
            this.afterBlendAdditives.some(x => x.id === material.id);

        if (alreadyExists) {
            return;
        }

        this.blendAdditives = [
            ...this.blendAdditives,
            {
                id: material.id,
                name: material.name ?? '',
                specificGravity: material.specificGravity ?? 0,
                absVolume: Math.round((material.absoluteVolume ?? 0) * 10000) / 10000,
                weight: 0,
                volume: 0,
                labWeight: 0,
                cisproContainer: ''
            }
        ];

        this.showBlendAdditiveSearch = false;
        this.blendAdditiveSearchText = '';
        this.blendAdditiveOptions = [];
        this.calculate();
    }
    onBlendAdditiveBlur(): void {
        setTimeout(() => { this.showBlendAdditiveSearch = false; this.blendAdditiveSearchText = ''; this.blendAdditiveOptions = []; }, 150);
    }
    removeBlendAdditive(item: DryMaterial): void {
        this.blendAdditives = this.blendAdditives.filter(m => m !== item);
        this.calculate();
    }

    showAddAfterBlendAdditiveSearch(): void {
        this.showAfterBlendAdditiveSearch = true;
        this.afterBlendAdditiveSearchText = '';
        this.afterBlendAdditiveOptions = [];
    }
    onAfterBlendAdditiveSearch(text: string): void { this.afterBlendAdditiveSubject.next(text); }
    selectAfterBlendAdditive(material: MaterialSearchResult): void {
        const alreadyExists =
            this.blendAdditives.some(x => x.id === material.id) ||
            this.afterBlendAdditives.some(x => x.id === material.id);

        if (alreadyExists) {
            return;
        }

        this.afterBlendAdditives = [
            ...this.afterBlendAdditives,
            {
                id: material.id,
                name: material.name ?? '',
                specificGravity: material.specificGravity ?? 0,
                absVolume: Math.round((material.absoluteVolume ?? 0) * 10000) / 10000,
                weight: 0,
                volume: 0,
                labWeight: 0,
                cisproContainer: ''
            }
        ];

        this.showAfterBlendAdditiveSearch = false;
        this.afterBlendAdditiveSearchText = '';
        this.afterBlendAdditiveOptions = [];
        this.calculate();
    }
    onAfterBlendAdditiveBlur(): void {
        setTimeout(() => { this.showAfterBlendAdditiveSearch = false; this.afterBlendAdditiveSearchText = ''; this.afterBlendAdditiveOptions = []; }, 150);
    }
    removeAfterBlendAdditive(item: DryMaterial): void {
        this.afterBlendAdditives = this.afterBlendAdditives.filter(m => m !== item);
        this.calculate();
    }

    showAddLiquidAdditiveSearch(): void {
        this.showLiquidAdditiveSearch = true;
        this.liquidAdditiveSearchText = '';
        this.liquidAdditiveOptions = [];
    }
    onLiquidAdditiveSearch(text: string): void { this.liquidSearchSubject.next(text); }
    selectLiquidAdditive(material: MaterialSearchResult): void {
        if (this.liquidAdditives.some(x => x.id === material.id)) {
            return;
        }

        this.liquidAdditives = [
            ...this.liquidAdditives,
            {
                id: material.id,
                name: material.name ?? '',
                volume: 0,
                specificGravity: material.specificGravity ?? 0,
                weight: 0,
                labWeight: 0,
                cisproContainer: ''
            }
        ];

        this.showLiquidAdditiveSearch = false;
        this.liquidAdditiveSearchText = '';
        this.liquidAdditiveOptions = [];
        this.calculate();
    }
    onLiquidAdditiveBlur(): void {
        setTimeout(() => { this.showLiquidAdditiveSearch = false; this.liquidAdditiveSearchText = ''; this.liquidAdditiveOptions = []; }, 150);
    }
    removeLiquidAdditive(item: LiquidMaterial): void {
        this.liquidAdditives = this.liquidAdditives.filter(m => m !== item);
        this.calculate();
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    editMaterial(material: any): void {
        const current: string[] = (material.cisproContainer ?? '')
            .split(',')
            .map((s: string) => s.trim())
            .filter((s: string) => s.length > 0);

        const dialogRef = this.dialog.open(SpacerCisproContainersDialogComponent, {
            width: '460px',
            disableClose: false,
            data: { containers: current }
        });

        dialogRef.afterClosed().subscribe((updated: string[] | undefined) => {
            if (updated !== undefined) {
                material.cisproContainer = updated.join(', ');
            }
        });
    }

    createFinalReport(): void {
        if (!this.spacerId || !this.projectId) return;

        const data: FinalReportDialogData = {
            projectId: this.projectId,
            projectTypeId: 2,
            slurries: [{
                slurryID: this.spacerId,
                ufSlurryID: this.selectedSpacer?.ufSlurryID ?? ''
            }],
            serviceCompanyName: '',
            userEmail: this.loggedUserService.getCurrentEmail() ?? ''
        };

        this.dialog.open(FinalReportDialogComponent, {
            width: 'fit-content',
            maxWidth: '96vw',
            maxHeight: '90vh',
            disableClose: true,
            data
        });
    }

    addTest(): void {
        if (!this.spacerId) {
            alert('Spacer ID is required to add a test.');
            return;
        }

        const dialogRef = this.dialog.open(AddTestDialogComponent, {
            width: '900px',
            data: { spacerId: this.spacerId, projectId: this.projectId } as AddTestDialogData
        });

        dialogRef.afterClosed().subscribe((result: AddTestDialogResult | null) => {
            if (result) {
                this.loadSpacerInfo(true);
                this.requestedTestsComp?.loadRequestedTests();
            }
        });
    }

    private validateBeforeSave(): string | null {
        if (!this.labVolume || this.labVolume <= 0) {
            return 'Lab volume is required and must be greater than 0.';
        }

        if (this.waterFractionBased) {
            if (!this.waterFraction || this.waterFraction <= 0) {
                return 'Water fraction is required for fraction-based spacers.';
            }
            if (!this.viscosifier || this.viscosifier <= 0) {
                return 'Viscosifier is required for fraction-based spacers.';
            }
        }

        const hasDryBase = this.selectedDryBaseId != null && this.blendBase.id != null;
        const hasLiquidBase = this.selectedLiquidBaseId != null && this.rigFreshWater.id != null;

        if (!hasDryBase && !hasLiquidBase) {
            return 'At least one base material (dry or liquid) is required.';
        }

        return null;
    }

    /**
     * NEW: Central place that mirrors WPF's post-save batch refresh
     * (Batches = clientBatches; NewBatch = CreateBatch(savedMix);) — call this
     * after every successful status-changing action so the Batches tab
     * (and canViewBatches gating) stay in sync with the saved mix.
     */
    private refreshBatchesAfterSave(): void {
        //force reload of current spacer to get updated status + batch count
        if (this.spacerId) { this.loadSpacerInfo(true); }
        this.batchesComponentRef?.reload();
    }

    completeSpacer(): void {
        if (!this.spacerId || this.isSaving) return;

        const confirmRef = this.dialog.open(ConfirmDialogComponent, {
            width: '420px',
            disableClose: true,
            data: {
                title: 'Complete Spacer',
                message: 'Are you sure you want to complete this spacer?',
                confirmLabel: 'Complete',
                cancelLabel: 'Cancel'
            }
        });

        confirmRef.afterClosed().subscribe((confirmed) => {
            if (!confirmed) return;

            this.isSaving = true;
            this.isLoading = true;
            this.saveMessage = null;

            this.spacerService.completeSpacer(this.spacerId!).subscribe({
                next: () => {
                    this.isSaving = false;
                    this.isLoading = false;
                    this.saveMessage = { text: 'Spacer completed successfully.', isError: false };
                    if (this.spacerId) { this.loadSpacerInfo(true); }
                    this.refreshBatchesAfterSave();
                    this.projectSaved.emit();
                    setTimeout(() => { this.saveMessage = null; }, 4000);
                },
                error: (err: any) => {
                    this.isSaving = false;
                    this.isLoading = false;
                    const detail = err?.error?.detail ?? err?.message ?? 'Unknown error';
                    this.dialog.open(ConfirmDialogComponent, {
                        width: '380px',
                        data: { title: 'Error', message: `Complete spacer failed: ${detail}`, confirmLabel: 'OK', cancelLabel: '' }
                    });
                }
            });
        });
    }

    updateSpacer(): void {
        if (!this.spacerId || this.isSaving) return;

        const error = this.validateBeforeSave();
        if (error) {
            this.dialog.open(ConfirmDialogComponent, {
                width: '380px',
                data: { title: 'Validation Error', message: error, confirmLabel: 'OK', cancelLabel: '' }
            });
            return;
        }

        this.isSaving = true;
        this.isLoading = true;
        this.saveMessage = null;

        const materials: UpdateSpacerMaterialDto[] = this.buildMaterialsArray();
        const request: UpdateSpacerRequest = this.buildUpdateRequest(materials);

        this.spacerService.updateSpacer(this.spacerId, request).subscribe({
            next: () => {
                this.isSaving = false;
                this.isLoading = false;
                this.saveMessage = { text: 'Spacer updated successfully.', isError: false };
                this.loadSpacerInfo(true);
                this.refreshBatchesAfterSave();
                this.projectSaved.emit();
                setTimeout(() => { this.saveMessage = null; }, 4000);
            },
            error: (err: any) => {
                this.isSaving = false;
                this.isLoading = false;
                const detail = err?.error?.detail ?? err?.message ?? 'Unknown error';
                this.dialog.open(ConfirmDialogComponent, {
                    width: '380px',
                    data: { title: 'Error', message: `Update failed: ${detail}`, confirmLabel: 'OK', cancelLabel: '' }
                });
            }
        });
    }

    private buildMaterialsArray(): UpdateSpacerMaterialDto[] {
        const materials: UpdateSpacerMaterialDto[] = [];

        if (this.selectedDryBaseId != null && this.blendBase.id != null) {
            materials.push({
                materialId: this.blendBase.id,
                sectionType: this.waterFractionBased ? 'SpacerDryFractionBase' : 'SpacerDryBase',
                weight: this.blendBase.weight,
                volume: this.blendBase.volume,
                calculatedLabWeight: this.blendBase.labWeight,
                afterBlend: false,
                byWeightOfTypeId: 0,
                preferredCisProContainerId: this.blendBase.cisproContainer || undefined
            });
        }

        for (const m of this.blendAdditives) {
            if (m.id == null) continue;
            materials.push({
                materialId: m.id,
                sectionType: 'Dry',
                weight: m.weight,
                volume: m.volume,
                calculatedLabWeight: m.labWeight,
                afterBlend: false,
                byWeightOfTypeId: 0,
                preferredCisProContainerId: m.cisproContainer || undefined
            });
        }

        for (const m of this.afterBlendAdditives) {
            if (m.id == null) continue;
            materials.push({
                materialId: m.id,
                sectionType: 'Dry',
                weight: m.weight,
                volume: m.volume,
                calculatedLabWeight: m.labWeight,
                afterBlend: true,
                byWeightOfTypeId: 0,
                preferredCisProContainerId: m.cisproContainer || undefined
            });
        }

        if (this.selectedLiquidBaseId != null && this.rigFreshWater.id != null) {
            materials.push({
                materialId: this.rigFreshWater.id,
                sectionType: this.waterFractionBased ? 'SpacerLiquidFractionBase' : 'SpacerLiquidBase',
                weight: this.rigFreshWater.weight,
                volume: this.rigFreshWater.volume,
                calculatedLabWeight: this.rigFreshWater.labWeight,
                overRideSp: this.rigFreshWater.specificGravity,
                afterBlend: false,
                byWeightOfTypeId: 0,
                preferredCisProContainerId: this.rigFreshWater.cisproContainer || undefined
            });
        }

        for (const m of this.liquidAdditives) {
            if (m.id == null) continue;
            materials.push({
                materialId: m.id,
                sectionType: 'Liquid',
                weight: m.weight,
                volume: m.volume,
                calculatedLabWeight: m.labWeight,
                overRideSp: m.specificGravity,
                afterBlend: false,
                byWeightOfTypeId: 0,
                preferredCisProContainerId: m.cisproContainer || undefined
            });
        }

        return materials;
    }

    private buildUpdateRequest(materials: UpdateSpacerMaterialDto[]): UpdateSpacerRequest {
        return {
            id: this.spacerId!,
            cementProjID: this.selectedSpacer?.cementProjID,
            ufSlurryID: this.selectedSpacer?.ufSlurryID,
            status: this.selectedSpacer?.status,
            type: this.selectedSpacer?.type,
            targetThickeningTimeFrMins: this.selectedSpacer?.targetThickeningTimeFrMins,
            targetThickeningTimeToMins: this.selectedSpacer?.targetThickeningTimeToMins,
            measuredDepth: this.selectedSpacer?.measuredDepth,
            slurryDensity: this.totalDensity ?? undefined,
            targetLabVolume: this.labVolume ?? undefined,
            equivalentSackWeight: this.selectedSpacer?.equivalentSackWeight,
            cementSackWeight: this.selectedSpacer?.cementSackWeight,
            waterFraction: this.waterFraction ?? undefined,
            viscosifier: this.viscosifier ?? undefined,
            mudWeight: this.selectedSpacer?.mudWeight,
            comments: this.spacerComments || undefined,
            jobType: this.selectedSpacer?.jobType,
            jobSubtype: this.selectedSpacer?.jobSubtype,
            pipeSize: this.selectedSpacer?.pipeSize,
            rigName: this.selectedSpacer?.rigName,
            wellName: this.selectedSpacer?.wellName,
            projectReceivedBy: this.selectedSpacer?.projectReceivedBy,
            sentToLabBy: this.selectedSpacer?.sentToLabBy,
            sentToLabDate: this.selectedSpacer?.sentToLabDate,
            isBlendTest: this.isBlendTest,
            preferredBlendCisPROContainerID: this.blendCisproContainer || undefined,
            preferredBlendCisPROContainerLocation: this.selectedSpacer?.preferredBlendCisPROContainerLocation,
            spacerTests: this.requestedTestsComp?.buildTestInputDto(),
            materials
        };
    }

    createSpacer(): void {
        // TODO: Implement create spacer
    }

    sendToLab(): void {
        if (!this.spacerId || this.isSaving) return;

        const confirmRef = this.dialog.open(ConfirmDialogComponent, {
            width: '420px',
            disableClose: true,
            data: {
                title: 'Send to Lab',
                message: 'Are you sure you want to send this spacer to lab?',
                confirmLabel: 'Send',
                cancelLabel: 'Cancel'
            }
        });

        confirmRef.afterClosed().subscribe((confirmed) => {
            if (!confirmed) return;

            this.isSaving = true;
            this.isLoading = true;
            this.saveMessage = null;

            const materials: UpdateSpacerMaterialDto[] = this.buildMaterialsArray();
            const saveRequest: UpdateSpacerRequest = this.buildUpdateRequest(materials);

            this.spacerService.updateSpacer(this.spacerId!, saveRequest).subscribe({
                next: () => {
                    this.spacerService.sendSpacerToLab(this.spacerId!).subscribe({
                        next: () => {
                            this.isSaving = false;
                            this.isLoading = false;
                            this.saveMessage = { text: 'Spacer sent to lab successfully.', isError: false };
                            if (this.spacerId) { this.loadSpacerInfo(true); }
                            this.refreshBatchesAfterSave();
                            this.projectSaved.emit();
                            setTimeout(() => { this.saveMessage = null; }, 4000);
                        },
                        error: (err: any) => {
                            this.isSaving = false;
                            this.isLoading = false;
                            const detail = err?.error?.detail ?? err?.message ?? 'Unknown error';
                            this.dialog.open(ConfirmDialogComponent, {
                                width: '380px',
                                data: { title: 'Error', message: `Send to lab failed: ${detail}`, confirmLabel: 'OK', cancelLabel: '' }
                            });
                        }
                    });
                },
                error: (err: any) => {
                    this.isSaving = false;
                    this.isLoading = false;
                    const detail = err?.error?.detail ?? err?.message ?? 'Unknown error';
                    this.dialog.open(ConfirmDialogComponent, {
                        width: '380px',
                        data: { title: 'Error', message: `Save before send failed: ${detail}`, confirmLabel: 'OK', cancelLabel: '' }
                    });
                }
            });
        });
    }

    revertToDraft(): void {
        if (!this.spacerId || this.isSaving) return;

        const confirmRef = this.dialog.open(ConfirmDialogComponent, {
            width: '420px',
            disableClose: true,
            data: {
                title: 'Revert to Draft',
                message: 'Are you sure you want to revert this spacer to draft?',
                confirmLabel: 'Revert',
                cancelLabel: 'Cancel'
            }
        });

        confirmRef.afterClosed().subscribe((confirmed) => {
            if (!confirmed) return;

            this.isSaving = true;
            this.isLoading = true;
            this.saveMessage = null;

            const materials: UpdateSpacerMaterialDto[] = this.buildMaterialsArray();
            const saveRequest: UpdateSpacerRequest = this.buildUpdateRequest(materials);

            this.spacerService.updateSpacer(this.spacerId!, saveRequest).subscribe({
                next: () => {
                    this.spacerService.revertSpacerToDraft(this.spacerId!).subscribe({
                        next: () => {
                            this.isSaving = false;
                            this.isLoading = false;
                            this.saveMessage = { text: 'Spacer reverted to draft successfully.', isError: false };
                            if (this.spacerId) { this.loadSpacerInfo(true); }
                            this.refreshBatchesAfterSave();
                            this.projectSaved.emit();
                            setTimeout(() => { this.saveMessage = null; }, 4000);
                        },
                        error: (err: any) => {
                            this.isSaving = false;
                            this.isLoading = false;
                            const detail = err?.error?.detail ?? err?.message ?? 'Unknown error';
                            this.dialog.open(ConfirmDialogComponent, {
                                width: '380px',
                                data: { title: 'Error', message: `Revert to draft failed: ${detail}`, confirmLabel: 'OK', cancelLabel: '' }
                            });
                        }
                    });
                },
                error: (err: any) => {
                    this.isSaving = false;
                    this.isLoading = false;
                    const detail = err?.error?.detail ?? err?.message ?? 'Unknown error';
                    this.dialog.open(ConfirmDialogComponent, {
                        width: '380px',
                        data: { title: 'Error', message: `Save before revert failed: ${detail}`, confirmLabel: 'OK', cancelLabel: '' }
                    });
                }
            });
        });
    }

    openAppendClearNoteDialog(): void {
        if (!this.spacerId) {
            return;
        }

        const dialogRef = this.dialog.open(SpacerAppendNoteDialogComponent, {
            width: '960px',
            disableClose: true,
            data: {
                spacerId: this.spacerId,
                originalNote: this.selectedSpacer?.comments ?? ''
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result || !result.success) {
                return;
            }

            if (this.spacerId) {
                this.loadSpacerInfo(true);
            }
        });
    }

    private getSelectedDryMaterialIds(): number[] {
        return [
            ...this.blendAdditives.map(x => x.id),
            ...this.afterBlendAdditives.map(x => x.id)
        ].filter((id): id is number => id != null);
    }

    private getSelectedLiquidMaterialIds(): number[] {
        return this.liquidAdditives
            .map(x => x.id)
            .filter((id): id is number => id != null);
    }

    // ══════════════════════════════════════════════════════════════
    // SECURITY / PERMISSIONS - Matches WPF SecurityControllerFactory
    // ══════════════════════════════════════════════════════════════

    /** Matches WPF line 135: mixId == 0 */
    get isNewMix(): boolean {
        return !this.spacerId;
    }

    /** Matches WPF line 140: mixId > 0 && (mixStatus == Draft || mixStatus == DraftAgain) */
    get canUpdateSpacer(): boolean {
        return !!this.spacerId &&
            (
                this.selectedSpacer?.status === SlurryStatuses.Draft ||
                this.selectedSpacer?.status === SlurryStatuses.DraftAgain
            ) &&
            this.selectedSpacer?.projectStatus !== ProjectStatuses.Completed;
    }

    /** Matches WPF line 146: mixId > 0 && (mixStatus == Draft || mixStatus == DraftAgain) */
    get canSendToLab(): boolean {
        const slurryStatus = this.selectedSpacer?.status;

        return !!this.spacerId &&
            (
                slurryStatus === SlurryStatuses.Draft ||
                slurryStatus === SlurryStatuses.DraftAgain
            ) &&
            this.selectedSpacer?.projectStatus !== ProjectStatuses.Completed;
    }

    /** Matches WPF line 151: mixId > 0 && (mixStatus == ReadytoTest || mixStatus == SentToLabAgain) */
    get canRevertToDraft(): boolean {
        const slurryStatus = this.selectedSpacer?.status;

        return !!this.spacerId &&
            (
                slurryStatus === SlurryStatuses.ReadytoTest ||
                slurryStatus === SlurryStatuses.SentToLabAgain
            ) &&
            this.selectedSpacer?.projectStatus !== ProjectStatuses.Completed;
    }

    /** Matches WPF line 156: mixStatus == OngoinginLab || ReadytoTest || SentToLabAgain */
    get canAddTest(): boolean {
        const slurryStatus = this.selectedSpacer?.status;

        return (
            slurryStatus === SlurryStatuses.OngoinginLab ||
            slurryStatus === SlurryStatuses.ReadytoTest ||
            slurryStatus === SlurryStatuses.SentToLabAgain
        ) &&
            this.selectedSpacer?.projectStatus !== ProjectStatuses.Completed;
    }

    /** Matches WPF line 162: mixStatus == OngoinginLab */
    get canAppendNote(): boolean {
        const slurryStatus = this.selectedSpacer?.status;

        return slurryStatus === SlurryStatuses.OngoinginLab &&
            this.selectedSpacer?.projectStatus !== ProjectStatuses.Completed;
    }

    /** Matches WPF SecurityControllerFactory line 167-169: 
 * Permission check for Complete button */
    get hasCompletePermission(): boolean {
        const slurryStatus = this.selectedSpacer?.status;

        // Spacer must exist, not be TestCompleted, and project not completed
        return !!this.spacerId &&
            slurryStatus !== SlurryStatuses.TestCompleted &&
            this.selectedSpacer?.projectStatus !== ProjectStatuses.Completed;
    }

    /** Matches WPF MixDetailViewModelBase line 774-791:
     * Business validation - all batch test outputs must be finalized */
    get allBatchTestsCompleted(): boolean {
        const outputs = this.requestedTestsComp?.getAllBatchOutputs() ?? [];
        if (outputs.length === 0) {
            return true;
        }
        // numeric statuses from BatchTestsBriefDto:
        // 1=InProgress, 4=Repeat/NeedRetake, 6=Completed(pending) → not done
        // 2=Approved, 3=Rejected, 5=Failed, 7=Cancelled → done
        return outputs.every(o => this.isBatchOutputFinalized(o.status));
    }

    private isBatchOutputFinalized(status: number): boolean {
        if (status === 7) return true;  // Cancelled
        if (status === 1 || status === 4 || status === 6) return false; // InProgress, NeedRetake, Completed(pending)
        return true; // Approved(2), Rejected(3), Failed(5)
    }

    /** Combined condition matching WPF Complete button logic exactly */
    get canCompleteSpacer(): boolean {
        return this.hasCompletePermission && this.allBatchTestsCompleted;
    }

    /** Matches WPF line 174: mixStatus == Draft || DraftAgain || OngoinginLab */
    get canEditMix(): boolean {
        const slurryStatus = this.selectedSpacer?.status;

        return (
            slurryStatus === SlurryStatuses.Draft ||
            slurryStatus === SlurryStatuses.DraftAgain ||
            slurryStatus === SlurryStatuses.OngoinginLab
        ) &&
            this.selectedSpacer?.projectStatus !== ProjectStatuses.Completed;
    }

    /** Base weight readonly when fraction-based (calculated) */
    get isBaseWeightReadonly(): boolean {
        return this.waterFractionBased;
    }

    /** Matches WPF line 108: IsEnabled="{Binding IsNewMix}" */
    get canChangeWaterFractionBased(): boolean {
        return this.isNewMix;
    }

    /** CisPRO containers disabled for blend test OR when not editable (matches WPF line 228) */
    get canEditMaterialContainers(): boolean {
        return !this.isBlendTest && this.canEditMix;
    }

    /** Matches WPF line 87 final report: ProjectCreateFinalRequest */
    get canCreateFinalReport(): boolean {
        return !!this.spacerId &&
            this.selectedSpacer?.projectStatus !== ProjectStatuses.Completed;
    }

    // ══════════════════════════════════════════════════════════════
    // TAB VISIBILITY - Matches WPF XAML lines 489-630
    // ══════════════════════════════════════════════════════════════

    /** Always visible */
    get canViewSpacerInfo(): boolean {
        return !!this.selectedSpacer;
    }

    /**
 * Matches WPF SecurityController line 205-206 exactly:
 * mixId > 0 && ((mixStatus == Draft/DraftAgain) || isActive)
 *            && (projectStatus != Completed || isActive)
 */
    get canViewRequestedTests(): boolean {
        const slurryStatus = this.selectedSpacer?.status;
        const projectCompleted = this.selectedSpacer?.projectStatus === ProjectStatuses.Completed;

        const statusCondition =
            slurryStatus === SlurryStatuses.Draft ||
            slurryStatus === SlurryStatuses.DraftAgain ||
            this.hasTests;

        const projectCondition = !projectCompleted || this.hasTests;

        return !!this.spacerId && statusCondition && projectCondition;
    }

    /**
     * Matches WPF SecurityController line 209: mixId > 0
     */
    get canViewMixability(): boolean {
        return !!this.spacerId;
    }

    /**
     * Matches WPF SecurityController line 211: mixId > 0
     */
    get canViewRedesign(): boolean {
        return !!this.spacerId;
    }

    /**
     * Matches WPF SecurityController line 217-219:
     * mixId > 0 && (mixStatus != TestCompleted || (mixStatus == TestCompleted && isBatchesExist))
     * Note: In WPF this tab is Visibility="Collapsed" by default (line 587)
     */
    get canViewBatches(): boolean {
        const slurryStatus = this.selectedSpacer?.status;

        return !!this.spacerId && (
            slurryStatus !== SlurryStatuses.TestCompleted ||
            (slurryStatus === SlurryStatuses.TestCompleted && this.hasBatches())
        );
    }

    /** NEW: real implementation backed by this.batchesExist (populated in loadSpacerInfo) */
    private hasBatches(): boolean {
        return this.batchesExist;
    }
}